import { ClipBoardTooltip } from "@/components/ClipBoardTooltip.tsx";
import { ContentPane } from "@/components/ContentPane.tsx";
import { NavigateAfterDelete } from "@/components/NavigateAfterDelete.tsx";
import { ResourceLink } from "@/components/ResourceLink.tsx";
import { ResourceWithIcon } from "@/components/ResourceWithIcon.tsx";
import { SectionHeader } from "@/components/SectionHeader.tsx";
import { Separator } from "@/components/Separator.tsx";
import { Spacer } from "@/components/Spacer.tsx";
import { ViewTitle } from "@/components/Title.tsx";
import { Toolbar } from "@/components/Toolbar.tsx";
import { WTabs } from "@/components/WTabs.tsx";
import { WTooltip } from "@/components/WTooltip.tsx";
import { DeleteButton } from "@/components/button/DeleteButton.tsx";
import { WButton } from "@/components/button/WButton.tsx";
import { LongDate } from "@/components/l10n/DateFormat.tsx";
import { DetailsHolder, DetailsTable } from "@/components/table/DetailsTable.tsx";
import { DetailsTableBillingAccount } from "@/components/table/DetailsTableBillingAccount.tsx";
import { DetailsTableName } from "@/components/table/DetailsTableName.tsx";
import { DetailsTableRow } from "@/components/table/DetailsTableRow.tsx";
import { WTable, WTableBody } from "@/components/table/WTable.tsx";
import { getResourceById } from "@/modules/api/resourceTypeException.tsx";
import { useStandardMutation } from "@/modules/api/useStandardMutation.ts";
import { LocationDetail } from "@/modules/location/LocationDetail.tsx";
import { AddResourceForm } from "@/modules/network/firewall/AddResourceForm.tsx";
import { AddRuleButton, EditRuleButton } from "@/modules/network/firewall/EditRuleModal.tsx";
import { FirewallDeleteModal } from "@/modules/network/firewall/FirewallDeleteModal.tsx";
import { convertTargetType } from "@/modules/network/firewall/firewall.logic.ts";
import {
    deleteFirewallMutation,
    firewallQueryAtom,
    modifyFirewallMutation,
    removeResourceFromFirewallMutation,
    type FirewallWithType,
} from "@/modules/network/firewall/firewallApi.ts";
import {
    allIpAddressesLabel,
    firewallInboundDescription,
    firewallOutboundDescription,
} from "@/modules/network/firewall/firewallLabels.tsx";
import { firewallResourcesAtom } from "@/modules/network/firewall/resources.ts";
import { getRuleType } from "@/modules/network/firewall/ruleSpec.tsx";
import { ResourceStatusBadge } from "@/modules/resource/ResourceStatusBadge.tsx";
import { getResourceName } from "@/modules/resource/getResourceName.ts";
import { useDeletableResource } from "@/utils/query/useDeletableResource.tsx";
import { useQueryResultAtom, useSuspenseQueryAtom } from "@/utils/query/useSuspenseQueryAtom.ts";
import { Trans, useLingui } from "@lingui/react/macro";
import type { BillingAccount, FirewallRuleDirectionType } from "@warrenio/api-spec/spec.oats.gen";
import { notNull } from "@warrenio/utils/notNull";
import { useState } from "react";

// NB: Extract the mutation call to a separate function so we can get its exact type via `typeof`
function useDeleteFirewallMutation() {
    return useStandardMutation(deleteFirewallMutation);
}

export type DeleteFirewallMutation = ReturnType<typeof useDeleteFirewallMutation>;

export function FirewallView({ location, firewallId }: { location: string; firewallId: string }) {
    //#region Hooks
    const { t } = useLingui();
    const deleteMutation = useDeleteFirewallMutation();
    const modifyMutation = useStandardMutation(modifyFirewallMutation);

    const data = useSuspenseQueryAtom(firewallQueryAtom(location));

    const firewall = useDeletableResource(() => getResourceById(data, firewallId, "firewall"), deleteMutation);
    //#endregion

    if (firewall === undefined) {
        return <NavigateAfterDelete />;
    }

    const { $type, uuid, display_name, created_at, billing_account_id, rules, resources_assigned } = firewall;

    async function changeBillingAccount(billing_account: BillingAccount) {
        const updatedFirewall = { ...firewall, billing_account_id: billing_account.id };
        await modifyMutation.mutateAsync({
            location,
            uuid,
            body: updatedFirewall,
        });
    }

    async function renameFirewall(display_name: string) {
        const updatedFirewall = { ...firewall, display_name };
        await modifyMutation.mutateAsync({ location, uuid, body: updatedFirewall });
    }

    return (
        <>
            <Toolbar>
                <FirewallDeleteModal item={firewall} deleteMutation={deleteMutation} />
            </Toolbar>

            <ContentPane className="VStack gap-4">
                <ViewTitle title={display_name ? display_name : ""} />

                <DetailsHolder>
                    <DetailsTable>
                        <DetailsTableName value={display_name} onChange={async (e) => await renameFirewall(e)} />
                        <DetailsTableRow title={t`Type`}>
                            <ResourceWithIcon type={$type} />
                        </DetailsTableRow>
                        <DetailsTableRow title={t`No. of rules`}>{rules.length}</DetailsTableRow>
                        <DetailsTableRow title={t`No. of resources`}>
                            {resources_assigned?.length ?? "0"}
                        </DetailsTableRow>
                    </DetailsTable>

                    <Spacer />

                    <DetailsTable>
                        <DetailsTableRow title={t`UUID`}>
                            <ClipBoardTooltip>{uuid}</ClipBoardTooltip>
                        </DetailsTableRow>
                        <DetailsTableRow title={t`Created`}>
                            <LongDate date={created_at} />
                        </DetailsTableRow>
                        <LocationDetail slug={location} />
                        <DetailsTableRow />
                        <DetailsTableBillingAccount
                            valueKey={billing_account_id}
                            onChange={async (item) => await changeBillingAccount(item.account)}
                        />
                    </DetailsTable>
                </DetailsHolder>
            </ContentPane>

            <Separator />

            <WTabs
                allTab
                items={[
                    {
                        id: "inbound_rules",
                        title: <Trans>Inbound rules</Trans>,
                        content: <RulesContent obj={firewall} direction="inbound" />,
                    },
                    {
                        id: "outbound_rules",
                        title: <Trans>Outbound rules</Trans>,
                        content: <RulesContent obj={firewall} direction="outbound" />,
                    },
                    { id: "target", title: <Trans>Resources</Trans>, content: <ResourcesContent obj={firewall} /> },
                ]}
            />
        </>
    );
}

function ResourcesContent({ obj }: { obj: FirewallWithType }) {
    //#region Hooks
    const { t } = useLingui();
    const removeMutation = useStandardMutation(removeResourceFromFirewallMutation);
    const [isVisible, setIsVisible] = useState(false);

    const { data: resources } = useQueryResultAtom(firewallResourcesAtom(obj.location));
    //#endregion

    const { location, uuid } = obj;

    async function removeResource(resource_uuid: string) {
        await removeMutation.mutateAsync({ location, uuid, vm_uuid: resource_uuid });
    }

    return (
        <ContentPane className="VStack gap-4">
            <SectionHeader title={<Trans>Resources</Trans>} />

            <WTable
                afterTable={
                    !isVisible && (
                        <WButton
                            color="primary"
                            variant="border"
                            size="bar"
                            icon="jp-icon-add"
                            action={() => setIsVisible(!isVisible)}
                        >
                            <Trans>Attach resource</Trans>
                        </WButton>
                    )
                }
            >
                <thead>
                    <tr>
                        <th>
                            <Trans>Name</Trans>
                        </th>
                        <th>
                            <Trans>Type</Trans>
                        </th>
                        <th>
                            <Trans>Private IP</Trans>
                        </th>
                        <th>
                            <Trans>Status</Trans>
                        </th>
                        <th />
                    </tr>
                </thead>
                <WTableBody>
                    {obj.resources_assigned?.map((item) => {
                        const resource = resources?.get(item.resource_uuid);
                        const ip = resource?.private_ipv4;

                        return (
                            <tr key={item.resource_uuid}>
                                <td>
                                    {resource && <ResourceLink title={getResourceName(resource)} item={resource} />}
                                </td>
                                <td>
                                    <ResourceWithIcon type={convertTargetType(item.resource_type)} />
                                </td>
                                <td>{!!ip && <ClipBoardTooltip>{ip}</ClipBoardTooltip>}</td>
                                <td>{!!resource && <ResourceStatusBadge item={resource} />}</td>
                                <td className="text-right">
                                    <DeleteButton
                                        label={t`Remove resource`}
                                        inTable
                                        action={async () => await removeResource(item.resource_uuid)}
                                    />
                                </td>
                            </tr>
                        );
                    })}
                </WTableBody>
                {isVisible && (
                    <tfoot>
                        <tr>
                            <td colSpan={5}>
                                <AddResourceForm item={obj} onClose={() => setIsVisible(false)} />
                            </td>
                        </tr>
                    </tfoot>
                )}
            </WTable>
        </ContentPane>
    );
}

function RulesContent({ obj, direction }: { obj: FirewallWithType; direction: FirewallRuleDirectionType }) {
    const { t } = useLingui();
    const modifyMutation = useStandardMutation(modifyFirewallMutation);

    const { location, uuid, rules } = obj;

    async function dropRule(rule_uuid: string) {
        const updatedRules = rules.filter((rule) => rule.uuid !== rule_uuid);
        await modifyMutation.mutateAsync({ location, uuid, body: { rules: updatedRules } });
    }

    const filteredRules = rules.filter((item) => item.direction === direction);

    return (
        <ContentPane className="VStack gap-4">
            <SectionHeader
                title={direction === "inbound" ? <Trans>Inbound rules</Trans> : <Trans>Outbound rules</Trans>}
                description={direction === "inbound" ? firewallInboundDescription : firewallOutboundDescription}
            />

            <WTable
                afterTable={
                    <AddRuleButton
                        location={location}
                        firewall={obj}
                        direction={direction}
                        button={
                            <WButton
                                isDisabled={rules.length >= 64}
                                color="primary"
                                variant="border"
                                size="bar"
                                icon="jp-icon-add"
                                action={undefined}
                            >
                                <Trans>Add rule</Trans>
                            </WButton>
                        }
                    />
                }
            >
                <thead>
                    <tr>
                        <th>
                            <Trans>Type</Trans>
                        </th>
                        <th>
                            <Trans>Protocol</Trans>
                        </th>
                        <th>
                            <Trans>Port range</Trans>
                        </th>
                        <th>{direction === "inbound" ? <Trans>Sources</Trans> : <Trans>Destinations</Trans>}</th>
                        <th />
                    </tr>
                </thead>
                <WTableBody>
                    {filteredRules.map((item) => {
                        const { uuid, protocol, port_start, port_end, endpoint_spec } = item;
                        return (
                            <tr key={uuid}>
                                <td>{getRuleType(item)}</td>
                                <td>{protocol === "any" ? <Trans>Any</Trans> : protocol.toUpperCase()}</td>
                                <td>
                                    {port_start === port_end ? (
                                        !port_start || port_start < 1 ? (
                                            "-"
                                        ) : (
                                            port_start
                                        )
                                    ) : (
                                        <>
                                            {port_start ? port_start < 1 ? "-" : port_start : <Trans>N/A</Trans>}
                                            {!!port_end && <> - {port_end}</>}
                                        </>
                                    )}
                                </td>
                                <td>{endpoint_spec?.length ? endpoint_spec.join(", ") : allIpAddressesLabel}</td>
                                <td className="text-right">
                                    <div className="flex gap-0.5em justify-end">
                                        <EditRuleButton
                                            location={location}
                                            firewall={obj}
                                            rule={item}
                                            button={
                                                <WTooltip text={t`Edit`}>
                                                    <WButton
                                                        color="primary"
                                                        size="xs"
                                                        variant="ghost"
                                                        icon="jp-icon-edit"
                                                        action={undefined}
                                                    />
                                                </WTooltip>
                                            }
                                        />

                                        <DeleteButton inTable action={async () => await dropRule(notNull(uuid))} />
                                    </div>
                                </td>
                            </tr>
                        );
                    })}
                </WTableBody>
            </WTable>
        </ContentPane>
    );
}
